import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import jwt_decode from "jwt-decode";
import LoginService from '../../services/login.service';
import OrganizationService from '../../services/organization.service';
import { useTranslation } from 'react-i18next';
import { UserType } from '../../types/Staff';
import SammiLogo from '../common/SammiLogo';
import Input from '../common/Input';
import Button from '../common/Button';
import { useSubscription } from '../common/subscription-context/SubscriptionContext';

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSubscriptionData, clearPastDue } = useSubscription();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [requiresOtp, setRequiresOtp] = useState(false);
  const [loginMsg, setLoginMsg] = useState<any>(null);
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [passwordResetRequested, setPasswordResetRequested] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    // Check for success message from registration
    if (location.state?.message) {
      setSuccessMsg(location.state.message);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (e) e.preventDefault();
    login();
  }

  const login = async () => {
    const loginResults = await LoginService.login(email, password, requiresOtp ? otp : undefined);
    
    // Check for 403 with TOTP required message
    if (loginResults.status === 403) {
      try {
        const errorData = await loginResults.json();
        if (errorData.message === "TOTP required or invalid") {
          setRequiresOtp(true);
          setLoginMsg("Please enter your 6-digit verification code");
          return;
        }
        // If we got here, it's a different 403 error - handle it normally
        setLoginMsg(errorData.message || t('login.error'));
        return;
      } catch (e) {
        // If parsing fails, show generic error
        setLoginMsg(t('login.error'));
        return;
      }
    }

    if (loginResults.ok) {
      const fetchedLogin = await loginResults.json();
      localStorage.setItem('accessToken', fetchedLogin.access_token);
      localStorage.setItem('refreshToken', fetchedLogin.refresh_token);
      localStorage.setItem('hasLoggedInBefore', 'true');

      const decoded: any = jwt_decode(fetchedLogin.access_token || '');

      if (decoded?.userType === UserType.MANAGER) {
        // Fetch organization details to check subscription status
        try {
          const orgDetails = await OrganizationService.getOrganizationDetails();
          
          if (orgDetails && orgDetails.subscriptionStatus === 'past_due') {
            // Store subscription status in context and localStorage
            setSubscriptionData('past_due', orgDetails.name || null);
            localStorage.setItem('pastDueFlag', 'true');
            
            // Redirect to past due payment page
            navigate('/past-due-payment');
            return;
          }
          
          // Normal flow — clear any stale past_due flags from a previous session
          clearPastDue();
          navigate('/sites');
        } catch (error) {
          console.error('Failed to fetch organization details:', error);
          // If we can't fetch org details, allow login but log the error
          // This prevents total app failure if the org endpoint is down
          navigate('/sites');
        }
      } else {
        alert('This login is for managers only. Please use the Fieldgistix Operator app to login as an operator.');
        navigate('/operator');
      }
    } else if (loginResults.status === 401) {
      setLoginMsg(t('login.error'));
    }
  };

  const requestPasswordReset = async () => {
    const response = await LoginService.requestPasswordReset(email);

    if (response.ok) {
      setPasswordResetRequested(true);
    }
  };

  return (
    <div 
      className="flex flex-col items-center justify-center min-h-screen px-4"
      style={{
        background: 'linear-gradient(135deg, rgba(25, 183, 223, 0.75) 0%, rgba(65, 239, 170, 0.75) 100%), linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}
    >
      <div className="flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 w-full max-w-md mx-auto">
        <SammiLogo size="large" className="mb-8" />
        
        <form className="w-full">
          <Input
            id="email"
            label={t('login.email')}
            type="email"
            value={email}
            onChange={(value: string) => setEmail(value)}
            placeholder={t('login.login')}
            autoComplete="username"
            required
            srOnlyLabel={true}
          />
          
          <Input
            id="password"
            label="Password"
            type="password"
            value={password}
            onChange={(value: string) => setPassword(value)}
            placeholder={t('login.password')}
            autoComplete="current-password"
            required
            srOnlyLabel={true}
          />
          
          {requiresOtp && (
            <Input
              id="otp"
              label="Verification Code"
              type="text"
              value={otp}
              onChange={(value: string) => {
                // Only allow numeric input and max 6 digits
                const numericValue = value.replace(/\D/g, '').slice(0, 6);
                setOtp(numericValue);
              }}
              placeholder="Enter 6-digit code"
              autoComplete="one-time-code"
              required
              srOnlyLabel={true}
            />
          )}
          
          {successMsg && (
            <div className="p-4 rounded-2xl bg-green-500 text-white mb-4 text-center text-sm">
              {successMsg}
            </div>
          )}
          
          {loginMsg && (
            <p className="text-red-500 mb-4 text-sm text-center">{loginMsg}</p>
          )}
          
          <Button
            variant="primary"
            type="submit"
            onClick={handleSubmit}
          >
            {t('login.login')}
          </Button>
        </form>

        {passwordResetRequested && (
          <div className="p-4 rounded-2xl bg-green-500 text-white mt-6 text-center text-sm">
            {t('login.resetPasswordMsg')}
          </div>
        )}

        <div className="flex flex-col items-center mt-6 space-y-3">
          <button type="button" className="transition-colors duration-200 text-sm" style={{ color: '#002457' }} onMouseEnter={(e) => (e.currentTarget as HTMLButtonElement).style.color = '#41EFAA'} onMouseLeave={(e) => (e.currentTarget as HTMLButtonElement).style.color = '#002457'} onClick={requestPasswordReset}>
            {t('login.forgotPassword')}
          </button>

          <Link to="/welcome" className="transition-colors duration-200 text-sm" style={{ color: '#002457', textDecoration: 'none' }} onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.color = '#41EFAA'} onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.color = '#002457'}>
            Create a new organization
          </Link>
        </div>
      </div>
    </div>
  );
}
